import {map} from 'rxjs/operators';
import {EventEmitter, Injectable, OnDestroy} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {BehaviorSubject, from, lastValueFrom, Observable, of, Subscription} from 'rxjs';
import {Credentials} from '../Model/User/Credentials';
import {AppUser} from '../Model/User/AppUser';
import {Customer} from '../Model/User/Customer';
import {ChangePassword} from '../Model/User/ChangePassword';
import {UserToCustomer} from '../Model/User/UserToCustomer';
import {RequestPassword} from '../Model/User/RequestPassword';
import {LanguageFlag} from '../Model/Dto/LanguageFlag';
import {CustomerLanguage} from '../Model/Dto/CustomerLanguage';
import {CustomerStatistics} from '../Model/Dto/CustomerStatistics';
import {TranslateService} from '@ngx-translate/core';
import {Toggle} from '../Model/ui/Toggle';
import {ViewService} from './view.service';
import {OsgConnection} from '../Model/Dto/OsgConnection';
import {SendToNWConfig} from '../Model/Dto/SendToNWConfig';
import {Message} from '../Model/System/Message';
import {SystemService} from './system.service';
import {WawiSettings} from '../Model/User/WawiSettings';
import {WawiSettingsService} from './wawiSettingsService.service';
import {CustomerService} from './customer.service';
import {UserService} from './user.service';
import {Router} from '@angular/router';
import {OAuthService} from 'angular-oauth2-oidc';
import {UserIdleService} from 'angular-user-idle';
import {ProductSearchOptions} from '../Model/User/ProductSearchOptions';
import {SearchFields} from '../Model/User/SearchFields';
import {AddonService} from './addon.service';
import {RegisterUser} from '../Model/User/RegisterUser';
import {SignalrService} from './signalr.service';
import {LicenseService} from './license.service';
import {License} from 'app/Model/User/License';

@Injectable()
export class LoginService implements OnDestroy {

    constructor(
        private oauthService: OAuthService,
        private userIdleService: UserIdleService,
        private http: HttpClient,
        private systemService: SystemService,
        public translate: TranslateService,
        public wawiSettingsService: WawiSettingsService,
        public customerService: CustomerService,
        private licenseService: LicenseService,
        public userService: UserService,
        private router: Router,
        private addonService: AddonService,
        private signalrService: SignalrService
    ) {
    }

    /*  customerList: Customer[];
      customer: Customer;
      customerClaim: string = "";*/
    public osgConnection: OsgConnection = new OsgConnection();
    public osgConnectionTest: OsgConnection = new OsgConnection();
    public wawiSettings: WawiSettings = new WawiSettings();
    public sendToNWConfig: SendToNWConfig = new SendToNWConfig();

    private idleStartSubscription?: Subscription;
    private idleEndSubscription?: Subscription;
    private idlePingSubscription?: Subscription;

    loggedIn: boolean = false;
    onLoggedIn: EventEmitter<void> = new EventEmitter<void>();
    onNewNewsAmountChanged: EventEmitter<void> = new EventEmitter<void>();

    private token = '';
    private tokenExpiration: Date;
    currentUser: AppUser;
    currentCustomer: Customer;
    selectedProductTabIndex: number = 0;

    private _currentCustomer$ = new BehaviorSubject<Customer>(null);

    get currentCustomer$(): Observable<Customer> {
      return this._currentCustomer$;
    }

    ngOnDestroy() {
        this.userIdleService.stopWatching();
        this.idleStartSubscription?.unsubscribe();
        this.idleEndSubscription?.unsubscribe();
        this.idlePingSubscription?.unsubscribe();
    }

    get isAdmin() {
        if (this.currentUser == null) {
            return false;
        }
        return this.currentUser.currentClaim == 'ADMIN';
    }

    get isUser(): boolean {
        if (this.currentUser == null) {
            return false;
        }
        return (
            this.currentUser.currentClaim == 'USER' ||
            this.currentUser.currentClaim == 'MANAGER' ||
            this.currentUser.currentClaim == 'ADMIN'
        );
    }

    get isManager(): boolean {
        if (this.currentUser == null) {
            return false;
        }
        return this.currentUser.currentClaim == 'MANAGER' || this.currentUser.currentClaim == 'ADMIN';
    }

    get productSearchOptions(): ProductSearchOptions {
        return this.currentUser.productSearchOptions;
    }

    set productSearchOptions(value: ProductSearchOptions) {
        this.currentUser.productSearchOptions = value;
    }

    get productSearchOptionsTarget(): ProductSearchOptions {
        return this.currentUser.productSearchOptions;
    }

    set productSearchOptionsTarget(value: ProductSearchOptions) {
        this.currentUser.productSearchOptions = value;
    }

    setCustomer(id: string): Promise<Customer> {
        return lastValueFrom(this.customerService.getCustomer(id)).then(async (result: Customer) => {
            if (this.currentCustomer) {
                this.signalrService.stopCustomerFeed(this.currentCustomer.id);
            }
            this.signalrService.startCustomerFeed(result.id);

            this.currentCustomer = result;
            this._currentCustomer$.next(this.currentCustomer);
            await this.updateLicense();

            return this.userService
                .getUser(this.currentUser.id, this.currentCustomer.id)
                .toPromise()
                .then((user: AppUser) => {
                    user.customerId = this.currentCustomer.id;
                    this.updateUser(user);
                    this.currentUser = user;
                    return this.updateAddons().then(async () => {
                        await this.getLanguages().toPromise();
                        this.setShow();
                        this.setLanguageFlags();
                        return result;
                    });
                });
        });
    }

    CheckAndSetFrontEndLanguage() {
        if (this.currentUser.frontEndLanguage) {
            if (this.translate.getCurrentLang() !== this.currentUser.frontEndLanguage) {
                this.translate.use(this.currentUser.frontEndLanguage);
            }
        }
    }

    getCustomerStatistics(customerId: string): Observable<CustomerStatistics[]> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', customerId)
        };
        return this.http.get<CustomerStatistics[]>('api/Account/getStatistics', options) as any;
    }

    get loginRequired(): boolean {
        return this.token.length == 0 || this.tokenExpiration > new Date();
    }

    get accessToken(): string {
        return this.oauthService.getAccessToken();
    }

    get maxUsers(): number {
        return this._license?.maxUsers ?? 0;
    }

    login(creds: Credentials, customerId: string) {
        return this.oauthService.fetchTokenUsingPasswordFlowAndLoadUserProfile(creds.username, creds.password).then(async () => {
            this.token = this.oauthService.getAccessToken();
            this.tokenExpiration = new Date(this.oauthService.getAccessTokenExpiration());

            let userId = this.oauthService.getIdentityClaims()['sub'];
            let customerId = this.oauthService.getIdentityClaims()['customerId'];

            await this.signalrService.startConnection("jobs", this.token);
            return this.userService
                .getUser(userId, customerId)
                .toPromise()
                .then((user: AppUser) => {
                    this.currentUser = user;
                }).catch(error => {
                    // userService missing
                    throw error;
                })
                .then(() =>
                    this.customerService
                        .getCustomer(customerId)
                        .toPromise()
                        .then(async (result: Customer) => {
                            if (this.currentCustomer) {
                                this.signalrService.stopCustomerFeed(this.currentCustomer.id);
                            }
                            this.signalrService.startCustomerFeed(result.id);

                            this.currentCustomer = result;
                            this._currentCustomer$.next(this.currentCustomer);
                            await this.updateLicense();

                            this.onLoggedIn.emit();
                            this.loggedIn = true;
                            return this.updateAddons().then(async () => {
                                await this.getLanguages().toPromise();
                                this.CheckAndSetFrontEndLanguage();

                                this.userIdleService.resetTimer();
                                this.userIdleService.startWatching();

                                this.idleStartSubscription = this.userIdleService.onTimerStart().subscribe((count) => {
                                    console.log(`User idle ${count}`);
                                });

                                this.idleEndSubscription = this.userIdleService.onTimeout().subscribe(async () => {
                                    console.log('Logged out because of idle timeout');
                                    await this.logout();
                                    this.router.navigate(['/login']);
                                });

                                this.idlePingSubscription = this.userIdleService.ping$.subscribe(() => {
                                    if (this.loggedIn) {
                                        console.log('Refreshing token');
                                        this.oauthService.refreshToken().then(async () => {
                                            let oldToken = this.token;
                                            this.token = this.oauthService.getAccessToken();
                                            this.tokenExpiration = new Date(this.oauthService.getAccessTokenExpiration());
                                            if (oldToken != this.token) {
                                                await this.signalrService.stopConnection(false);
                                                await this.signalrService.startConnection("jobs", this.token);
                                            }
                                        });
                                    }
                                });

                                return true;
                            });
                        })
                ).catch(error => {
                    // customerService missing
                    error.error ??= {};
                    error.error.error_description = "Nutzer keinem Account zugeordnet.";
                    throw error;
                });
        });
    }

    silentlogin(customerId: string) {
        if (this.oauthService.getRefreshToken()) {
            return from(
                this.oauthService
                    .refreshToken()
                    .then(() => {
                        return this.oauthService.loadUserProfile();
                    })
                    .then(async () => {
                        this.token = this.oauthService.getAccessToken();
                        this.tokenExpiration = new Date(this.oauthService.getAccessTokenExpiration());

                        let userId = this.oauthService.getIdentityClaims()['sub'];
                        let customerId = this.oauthService.getIdentityClaims()['customerId'];

                        await this.signalrService.startConnection("jobs", this.token);

                        return this.userService
                            .getUser(userId, customerId)
                            .toPromise()
                            .then((user: AppUser) => {
                                this.currentUser = user;
                            })
                            .then(() =>
                                this.customerService
                                    .getCustomer(customerId)
                                    .toPromise()
                                    .then(async (result: Customer) => {
                                        if (this.currentCustomer) {
                                            this.signalrService.stopCustomerFeed(this.currentCustomer.id);
                                        }
                                        this.signalrService.startCustomerFeed(result.id);

                                        this.currentCustomer = result;
                                        this._currentCustomer$.next(this.currentCustomer);
                                        await this.updateLicense();

                                        this.onLoggedIn.emit();
                                        this.loggedIn = true;
                                        return this.updateAddons().then(async () => {
                                            await this.getLanguages();
                                            this.CheckAndSetFrontEndLanguage();

                                            this.userIdleService.resetTimer();
                                            this.userIdleService.startWatching();

                                            this.idleStartSubscription = this.userIdleService.onTimerStart().subscribe((count) => {
                                                console.log(`User idle ${count}`);
                                            });

                                            this.idleEndSubscription = this.userIdleService.onTimeout().subscribe(async () => {
                                                console.log('Logged out because of idle timeout');
                                                await this.logout();
                                                this.router.navigate(['/login']);
                                            });

                                            this.idlePingSubscription = this.userIdleService.ping$.subscribe(() => {
                                                if (this.loggedIn) {
                                                    console.log('Refreshing token');
                                                    this.oauthService.refreshToken().then(async () => {
                                                        let oldToken = this.token;
                                                        this.token = this.oauthService.getAccessToken();
                                                        this.tokenExpiration = new Date(this.oauthService.getAccessTokenExpiration());
                                                        if (oldToken != this.token) {
                                                            await this.signalrService.stopConnection(false);
                                                            await this.signalrService.startConnection("jobs", this.token);
                                                        }
                                                    });
                                                }
                                            });

                                            return true;
                                        });
                                    })
                            );
                    })
            );
        } else {
            return of(false);
        }
    }

    async logout() {
        if (this.currentCustomer) {
            this.signalrService.stopCustomerFeed(this.currentCustomer.id);
        }
        await this.signalrService.stopConnection(true);

        this.idleStartSubscription?.unsubscribe();
        this.idleEndSubscription?.unsubscribe();
        this.idlePingSubscription?.unsubscribe();

        this.userIdleService.stopWatching();
        this.oauthService.logOut();

        this.loggedIn = false;
        this.token = '';
        this.tokenExpiration = null;
        this.currentUser = null;
    }

    getCurrentCustomer(): Observable<Customer> {
        return this.customerService.getCustomer(this.currentUser.customerId);
    }

    notifyTestAccount() {
        this.notifyTestAccountForCustomer(this.currentCustomer);
    }

    notifyTestAccountForCustomer(customer: Customer) {
        if (customer.isTestAccount) {
            let now = new Date(Date.now());
            let end = new Date(customer.accountValidDate.toString());

            //https://stackoverflow.com/a/51861734
            let diff = Math.abs(now.getTime() - end.getTime());
            let remaining = Math.floor(diff / (1000 * 60 * 60 * 24)) + 1; // / 365.25);       // oder nicht +2? was ist mit sommer/winterzeit?

            if (end.getTime() < now.getTime()) {
                this.translate.get('TestLoginHintExpired').subscribe((text: string) => {
                    let translation = text;
                    this.systemService.notify(new Message(translation, 'info'), 10000);
                });
            } else {
                this.translate.get('TestLoginHint').subscribe((text: string) => {
                    let translation = text.replace('{{days}}', remaining.toString());
                    this.systemService.notify(new Message(translation, 'info'), 10000);
                });
            }
        }
    }

    async updateLicense() {
        this._license = await lastValueFrom<License>(this.licenseService.getLicenseForCustomer(this.currentCustomer.id));
        this._license$.next(this._license);
    }

    async updateAddons() {
        const addons = await lastValueFrom<string[]>(this.addonService.getAddonsOfCustomer(this.currentCustomer.id));
        await this.setAddons(addons);
    }

    getLanguages(): Observable<void> {
        const options = {
            headers: new HttpHeaders().append('Content-Type', 'application/json'),
            params: new HttpParams().append('customerId', this.currentUser.customerId)
        };

        return this.http.get('api/AccountLanguage/GetCustomerLanguages', options).pipe(
            map((result: CustomerLanguage[]) => {
                this.setLanguages(result);
            })
        );
    }

    register(registerUser: RegisterUser): Promise<string> {
        return lastValueFrom(this.http.post('api/account/register', registerUser) as Observable<string>);
    }

    updateUser(user: AppUser = null) {
        if (user == null) user = this.currentUser;

        this.userService.update(user).subscribe((result: AppUser) => {
            this.currentUser = result;
        });
    }

    changePassword(changePassword: ChangePassword) {
        return this.http.post('api/account/ChangePassword', changePassword).pipe(
            map((response) => {
                return true;
            })
        );
    }

    requestPassword(email: RequestPassword) {
        return this.http.post('api/account/requestPassword', email).pipe(
            map((response) => {
                return true;
            })
        );
    }

    updateSearchFields(
        selectedSearchFields: string[],
        searchMode: string,
        userId: string,
        isTarget: boolean,
        searchStateMode: 'or' | 'and' = null
    ): Observable<ProductSearchOptions> {
        let options = new ProductSearchOptions();
        options.searchFields = [];
        options.searchMode = searchMode;
        options.userId = userId;
        options.isTarget = isTarget;
        options.searchStateMode = searchStateMode;
        selectedSearchFields.forEach((value) => {
            let field = new SearchFields();
            field.searchField = value;
            options.searchFields.push(field);
        });
        return this.http.post('api/user/updateSearchFields', options).pipe(
            map((result: ProductSearchOptions) => {
                return result;
            })
        );
    }

    /*******************************************************************************/
    /*                            ACCOUNT                                          */
    /*******************************************************************************/

    private _customers: UserToCustomer[];

    private _license?: License;
    private _license$ = new BehaviorSubject<License>(null);

    public get license$(): Observable<License> {
      return this._license$;
    }

    private _addons$ = new BehaviorSubject([]);

    get addons$(): Observable<string[]> {
      return this._addons$;
    }

    addons: string[] = [];

    hasAddonCSV = false;
    hasAddonMULTI_ACCOUNT = false;
    hasAddonEXCEL_ORDER_EXPORT = false;
    hasAddonAGREEMENT = false;
    hasAddonCATALOG_DETAILS = false;
    hasAddonVALIDATION = false;
    hasAddonUDX = false;
    hasAddonTranslate = false;
    hasAddonPRODUCT_DETAILS = false;
    hasAddonSEO = false;
    hasAddonDATA_QUALITY = false;
    hasAddonPRINT = false;
    hasAddonOSG = false;

    hasAddonEDENUMBERS = false;

    hasAddonETIM = false;
    hasAddonECLASS = false;

    hasAddonNWCLASS = false;
    hasAddonECLASS_FREE = false;
    hasAddonShopware6 = false;
    hasAddonScireum = false;
    hasAddonNordwest = false;
    hasAddonProductTemplates = false;
    hasAddonEBAY_CLASSIFICATION = false;
    hasAddonEBAY_EXPORT = false;
    hasAddonCATEGORYEXPORT = false;
    hasAddonREPORTPRODUCTDATASHEET = false;
    hasAddonDEEPLTRANSLATE = false;
    hasAddonDEEPLTRANSLATEOWNKEY = false;
    hasAddonDEVELOPMENT = false;
    hasAddonWawiErp = false;
    hasAddonGevis = false;
    hasAddonEDESortimentsAbo = false;
    hasAddonRClass = false;
    hasAddonMassDataChange = false;
    hasAddonKI_TEXTS = false;
    hasAddonSYMBIONET = false;
    hasAddonETIM5 = false;
    hasAddonSHOPWARE5 = false;
    hasAddonSMARTSTORE = false;
    hasAddonVIRTUAL_CATALOGS = false;
    hasAddonSUPPLIERS = false;
    hasAddonODOO = false;
    hasAddonEDC = false;
    hasAddonINTERSHOP = false;
    hasAddonSHOPWARE6_ASYNCINDEXING = false;
    hasAddonEIS_CLASS = false;
    hasAddonElectronicSales = false;
    hasAddonAdvancedTemplate = false;
    hasAddonDataSharing = false;
    hasAddonDataSources = false;
    hasAddonAutoJobsImport = false;
    hasAddonAutoJobsExport = false;
    hasAddonCsCard = false;
    hasAddonOXOMI = false;
    hasAddonSurcharge = false;
    hasAddonPackaging = false;
    hasAddonWorkflow = false;
    hasAddonDataProcess = false;
    hasAddonKI_CLASSIFAI = false;
    hasAddonONBOARDING = false;
    hasAddonSellsite_Neu = false;
    hasAddonSellsite_Bormann = false;
    hasAddonVEO = false;
    hasAddonScrapper = false;
    hasAddonBauvista = false;
    hasAddonMultipleImportTemplateFields = false;

    async setAddons(addons: string[]) {
        this.hasAddonEDC = false;
        this.hasAddonRClass = false;
        this.hasAddonOSG = false;
        this.hasAddonCSV = false;
        this.hasAddonMULTI_ACCOUNT = false;
        this.hasAddonAGREEMENT = false;
        this.hasAddonEXCEL_ORDER_EXPORT = false;
        this.hasAddonPackaging = false;
        this.hasAddonCATALOG_DETAILS = false;
        this.hasAddonVALIDATION = false;
        this.hasAddonUDX = false;
        this.hasAddonTranslate = false;
        this.hasAddonPRODUCT_DETAILS = false;
        this.hasAddonSEO = false;
        this.hasAddonDATA_QUALITY = false;
        this.hasAddonPRINT = false;
        this.hasAddonEDENUMBERS = false;
        this.hasAddonETIM = false;
        this.hasAddonECLASS = false;
        this.hasAddonNWCLASS = false;
        this.hasAddonShopware6 = false;
        this.hasAddonScireum = false;
        this.hasAddonNordwest = false;
        this.hasAddonProductTemplates = false;
        this.hasAddonEBAY_CLASSIFICATION = false;
        this.hasAddonEBAY_EXPORT = false;
        this.hasAddonCATEGORYEXPORT = false;
        this.hasAddonREPORTPRODUCTDATASHEET = false;
        this.hasAddonDEEPLTRANSLATE = false;
        this.hasAddonDEEPLTRANSLATEOWNKEY = false;
        this.hasAddonDEVELOPMENT = false;
        this.hasAddonWawiErp = false;
        this.hasAddonGevis = false;
        this.hasAddonEDESortimentsAbo = false;
        this.hasAddonMassDataChange = false;
        this.hasAddonKI_TEXTS = false;
        this.hasAddonSHOPWARE5 = false;
        this.hasAddonSMARTSTORE = false;
        this.hasAddonVIRTUAL_CATALOGS = false;
        this.hasAddonSUPPLIERS = false;
        this.hasAddonODOO = false;
        this.hasAddonSHOPWARE6_ASYNCINDEXING = false;
        this.hasAddonINTERSHOP = false;
        this.hasAddonEIS_CLASS = false;
        this.hasAddonElectronicSales = false;
        this.hasAddonAdvancedTemplate = false;
        this.hasAddonDataSharing = false;
        this.hasAddonAutoJobsImport = false;
        this.hasAddonAutoJobsExport = false;
        this.hasAddonDataSources = false;
        this.hasAddonCsCard = false;
        this.hasAddonOXOMI = false;
        this.hasAddonSurcharge = false;
        this.hasAddonPackaging = false;
        this.hasAddonWorkflow = false;
        this.hasAddonDataProcess = false;
        this.hasAddonKI_CLASSIFAI = false;
        this.hasAddonSYMBIONET = false;
        this.hasAddonETIM5 = false;
        this.hasAddonONBOARDING = false;
        this.hasAddonVEO = false;
        this.hasAddonScrapper = false;
        this.hasAddonBauvista = false;
        this.hasAddonMultipleImportTemplateFields = false;
        const promises = [];
        for (let entry of addons) {
            promises.push(this.setAddonByString(entry));
        }
        await Promise.all(promises);

        this.addons = addons;
        this._addons$.next(this.addons);
    }

    private async setAddonByString(entry: string) {
        switch (entry) {
            case 'CSV':
                this.hasAddonCSV = true;
                break;
            case 'SCRAPPER':
                this.hasAddonScrapper = true;
                break;
            case 'PACKAGING':
                this.hasAddonPackaging = true;
                break;
            case 'EIS_CLASS':
                this.hasAddonEIS_CLASS = true;
                break;
            case 'EXCEL_ORDER_EXPORT':
                this.hasAddonEXCEL_ORDER_EXPORT = true;
                break;
            case 'EDC':
                this.hasAddonEDC = true;
                break;
            case 'RCLASS':
                this.hasAddonRClass = true;
                break;
            case 'SUPPLIERS':
                this.hasAddonSUPPLIERS = true;
                break;
            case 'WAWI_ERP':
                this.hasAddonWawiErp = true;
                this.wawiSettings = await lastValueFrom<WawiSettings>(
                    this.wawiSettingsService.getWawiSettings(this.currentCustomer.id)
                );
                break;
            case 'CATEGORY_EXPORT':
                this.hasAddonCATEGORYEXPORT = true;
                break;
            case 'REPORT_PRODUCT_DATASHEET':
                this.hasAddonREPORTPRODUCTDATASHEET = true;
                break;
            case 'DEEPL_TRANSLATE':
                this.hasAddonDEEPLTRANSLATE = true;
                break;
            case 'DEEPL_TRANSLATE_OWN_KEY':
                this.hasAddonDEEPLTRANSLATEOWNKEY = true;
                break;
            case 'SHOPWARE_6':
                this.hasAddonShopware6 = true;
                break;
            case 'SCIREUM':
                this.hasAddonScireum = true;
                break;
            case 'SELLSITE_NEU':
                this.hasAddonSellsite_Neu = true;
                break;
            case 'SELLSITE_BORMANN':
                this.hasAddonSellsite_Bormann = true;
                break;
            case 'MULTI_ACCOUNT':
                this.hasAddonMULTI_ACCOUNT = true;
                break;
            case 'OSG':
                this.hasAddonOSG = true;
                break;
            case 'DATA_QUALITY':
                this.hasAddonDATA_QUALITY = true;
                break;
            case 'SEO':
                this.hasAddonSEO = true;
                break;
            case 'AGREEMENT':
                this.hasAddonAGREEMENT = true;
                break;
            case 'CATALOG_DETAILS':
                this.hasAddonCATALOG_DETAILS = true;
                break;
            case 'VALIDATION':
                this.hasAddonVALIDATION = true;
                break;
            case 'UDX':
                this.hasAddonUDX = true;
                break;
            case 'NORDWEST':
                this.hasAddonNordwest = true;
                break;
            case 'TRANSLATE':
                this.hasAddonTranslate = true;
                break;
            case 'PRODUCT_DETAILS':
                this.hasAddonPRODUCT_DETAILS = true;
                break;
            case 'PRINT':
                this.hasAddonPRINT = true;
                break;
            case 'EDENUMBERS':
                this.hasAddonEDENUMBERS = true;
                break;
            case 'ETIM':
                this.hasAddonETIM = true;
                break;
            case 'ECLASS':
                this.hasAddonECLASS = true;
                break;
            case 'ECLASS_FREE':
                this.hasAddonECLASS_FREE = true;
                break;
            case 'NWCLASS':
                this.hasAddonNWCLASS = true;
                break;
            case 'PRODUCT_TEMPLATE':
                this.hasAddonProductTemplates = true;
                break;
            case 'EBAY_CLASSIFICATION':
                this.hasAddonEBAY_CLASSIFICATION = true;
                break;
            case 'EBAY_EXPORT':
                this.hasAddonEBAY_EXPORT = true;
                break;
            case 'GEVIS':
                this.hasAddonGevis = true;
                break;
            case 'EDE_SORTIMENTSABO':
                this.hasAddonEDESortimentsAbo = true;
                break;
            case 'MASSDATACHANGE':
                this.hasAddonMassDataChange = true;
                break;
            case 'DEVELOPMENT':
                this.hasAddonDEVELOPMENT = true;
                break;
            case 'KI_TEXTS':
                this.hasAddonKI_TEXTS = true;
                break;
            case 'SYMBIONET':
                this.hasAddonSYMBIONET = true;
            break;
            case 'ETIM5':
              this.hasAddonETIM5 = true;
              break;
            case 'KI_CLASSIFAI':
                this.hasAddonKI_CLASSIFAI = true;
                break;
            case 'SHOPWARE_5':
                this.hasAddonSHOPWARE5 = true;
                break;
            case 'SMARTSTORE':
                this.hasAddonSMARTSTORE = true;
                break;
            case 'VIRTUAL_CATALOGS':
                this.hasAddonVIRTUAL_CATALOGS = true;
                break;
            case 'ODOO':
                this.hasAddonODOO = true;
                break;
            case 'SHOPWARE6_ASYNCINDEXING':
                this.hasAddonSHOPWARE6_ASYNCINDEXING = true;
                break;
            case 'INTERSHOP':
                this.hasAddonINTERSHOP = true;
                break;
            case 'ELECTRONIC_SALES':
                this.hasAddonElectronicSales = true;
                break;
            case 'ADVANCED_TEMPLATE':
                this.hasAddonAdvancedTemplate = true;
                break;
            case 'DATA_SHARING':
                this.hasAddonDataSharing = true;
                break;
            case 'AUTO_JOBS_IMPORT':
                this.hasAddonAutoJobsImport = true;
                break;
            case 'AUTO_JOBS_EXPORT':
                this.hasAddonAutoJobsExport = true;
                break;
            case 'DATASOURCES':
                this.hasAddonDataSources = true;
                break;
            case 'CSCARD':
                this.hasAddonCsCard = true;
                break;
            case 'OXOMI':
                this.hasAddonOXOMI = true;
                break;
            case 'SURCHARGE':
                this.hasAddonSurcharge = true;
                break;
            case 'WORKFLOW':
                this.hasAddonWorkflow = true;
                break;
            case 'DATAPROCESS':
                this.hasAddonDataProcess = true;
                break;
            case 'ONBOARDING':
                this.hasAddonONBOARDING = true;
                break;
            case "VEO":
                this.hasAddonVEO = true;
                break;
            case "BAUVISTA":
                this.hasAddonBauvista = true;
                break;
            case "MULTIPLE_IMPORT_TEMPLATEFIELDS":
                this.hasAddonMultipleImportTemplateFields = true;
                break;
        }
    }

    customerLanguages: CustomerLanguage[] = [];

    setLanguages(languges: CustomerLanguage[]) {
        this.customerLanguages = languges;
        this.setLanguageFlags();
    }

    addLanguage(language) {
        this.customerLanguages.push(language);
        this.setLanguageFlags();
    }

    selectedTranslateLanguage = 'deu';
    languageFlags = new Array<LanguageFlag>();

    getLanguageText(language) {
        const key = ViewService.languages.indexOf(((languageTemplate) => languageTemplate.code == language) as any);

        if (key != -1) return ViewService.languages[key].language;

        return 'Deutsch';
    }

    getLanguageFlag(language) {
        const key = ViewService.languages.findIndex((languageTemplate) => languageTemplate.code == language);

        if (key != -1) return ViewService.languages[key].flag;

        return 'de.gif';
    }

    setLanguageFlags() {
        this.languageFlags = this.setLanguageFlagsByCustomerLanguage(this.customerLanguages);
    }

    setLanguageFlagsByCustomerLanguage(customerLanguages): LanguageFlag[] {
        const languageFlags = new Array<LanguageFlag>();
        let foundElement = ViewService.languages.find((element) => element.code == this.currentCustomer.defaultLanguage);
        if (foundElement) languageFlags.push(foundElement);

        for (let language of customerLanguages) {
            foundElement = ViewService.languages.find((element) => element.code == language.language);

            if (foundElement) languageFlags.push(foundElement);
        }
        return languageFlags;
    }

    getLocaleFromFrontEndLanguage() {
        let lang = this.currentUser.frontEndLanguage;

        let locale = 'de-DE';
        switch (lang) {
            //case "de": locale = "de-DE"; break;
            case 'en':
                locale = 'en-GB';
                break;
            case 'nl':
                locale = 'nl-NL';
                break;
            default:
                locale = 'de-DE';
                break;
        }

        return locale;
    }

    /*******************************************************************************/
    /*                            SETTINGS                                         */

    /*******************************************************************************/

    get showInfoText() {
        if (this.currentUser) return this.currentUser.showInfoText;
        return true;
    }

    set showInfoText(value: boolean) {
        if (this.currentUser) {
            this.currentUser.showInfoText = value;
            this.userService.update(this.currentUser);
        }
    }

    get showToolTips() {
        if (this.currentUser) return this.currentUser.showToolTips;
        return true;
    }

    set showToolTips(value: boolean) {
        if (this.currentUser) {
            this.currentUser.showToolTips = value;
            this.userService.update(this.currentUser);
        }
    }

    get showProToolTips() {
        if (this.currentUser) return this.currentUser.showProToolTips;
        return true;
    }

    set showProToolTips(value: boolean) {
        if (this.currentUser) {
            this.currentUser.showProToolTips = value;
            this.userService.update(this.currentUser);
        }
    }

    get showPlaceHolders() {
        if (this.currentUser) return this.currentUser.showPlaceHolders;
        return true;
    }

    set showPlaceHolders(value: boolean) {
        if (this.currentUser) {
            this.currentUser.showPlaceHolders = value;
            this.userService.update(this.currentUser);
        }
    }

    setShow() {
        this.showHTMLEditor.state = true;
        this.showGroupReference.state = this.currentUser.showGroupReference;
    }

    public showHTMLEditor = new Toggle(true);
    public showGroupReference = new Toggle(false);

    saveShow() {
        this.currentUser.showGroupReference = this.showGroupReference.state;
    }

    get viewPort() {
        if (this.currentUser) {
            return this.currentUser.viewPort;
        }
        return false;
    }

    set viewPort(value: boolean) {
        if (this.currentUser) {
            this.currentUser.viewPort = value;
            this.userService.update(this.currentUser);
        }
    }

    getViewPort(): string {
        if (this.viewPort) {
            return 'containerWidth';
        }
        return '';
    }

    set lastNewRead(value: Date) {
        if (this.currentUser) {
            this.userService
                .setLastNewsRead(this.currentUser.id, value)
                .toPromise()
                .then((result: AppUser) => {
                    this.currentUser = result;
                    this.onNewNewsAmountChanged.emit();
                });
        }
    }

    get lastNewRead(): Date {
        if (this.currentUser) {
            if (this.currentUser.lastNewsRead instanceof Date) {
                return this.currentUser.lastNewsRead;
            }
            return new Date(Date.parse(this.currentUser.lastNewsRead));
        }
        return null;
    }
}
